import { DataFlowSource, CompletionAction, DocumentForm } from 'app/model/dataflow/dataflow-source';
import { DataFlowExpressionUtils } from '../../expression.util';
import {
    IntegerDataFlowType,
    TimeStampDataFlowType,
    BooleanDataFlowType,
    StringDataFlowType,
    NumberDataFlowType,
    LongDataFlowType
} from 'app/model/dataflow/dataflow-datatype';
import { ColumnReference } from 'app/model/dataset/columnReference';
import { SchemaConvertor } from './schema.convertor';
import { TransformConvertorBase } from './transform.convertor';
import { Transformation } from 'app/model/dataflow/transformation';
import { SourcePlainObject } from 'worker/app-workers/shared/plainObjects/source.plainObject';
import {
    AdapterContext,
    AzureDataExplorerDataset,
    DataFlowParameterConfig,
    DatasetFormatType,
    IResourceContext,
    Metamodel,
    SapODataDataset,
    stringIsNullOrEmpty
} from 'shell-models-lib';
import { DataFlowAdapterUtils } from '../../adapter.util';
import { SapOdpSettings } from 'app/model/dataflow/dataset-settings/sap-odp-settings';
import { SapOdpDataset } from 'app/model/dataset/sap-odp.dataset';
import { DynamicsEntityDataset } from 'app/model/dataset/dynamics-entity.dataset';
import { DynamicsCrmEntityDataset } from 'app/model/dataset/dynamics-crm.dataset';
import { CommonDataServiceEntityDataset } from 'app/model/dataset/common-data-service.dataset';
import { SapOdpPartitionPredicate } from 'app/model/dataflow/sap-odp-partition-predicate';
import { DataFlowExpressionArray } from 'app/model/dataflow/dataflow-expression-array';
import { CdmSettings } from 'app/model/dataflow/dataset-settings/cdm-settings';
import { EventHubsSettings } from 'app/model/dataflow/dataset-settings/event-hubs-settings';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { DataflowFormatRegistryService } from 'app/registries/dataflow/dataflow-format-registry.service';

export class SourceConvertor extends TransformConvertorBase {
    private get source(): DataFlowSource {
        return this.transformation as DataFlowSource;
    }

    private get sourceObject(): SourcePlainObject {
        return this.transformObject as SourcePlainObject;
    }

    constructor(sourceObject: SourcePlainObject, source: DataFlowSource) {
        super(sourceObject, source);
    }

    public async convert(contextData: IResourceContext): Promise<Transformation> {
        await super.convert(contextData);
        this.source.schema = SchemaConvertor.convertColumnsFromPlainObject(this.sourceObject.columns, this.source);

        // process inline settings first
        for (const configName of ['store', 'format']) {
            if (this.sourceObject.configs.has(configName)) {
                const expression = this.sourceObject.configs.get(configName);
                const format = DataFlowExpressionUtils.extractStringConfigValue(expression);
                this._handledConfigs.push(configName);
                const dataflowFormatRegistryService = await getServiceInstance<DataflowFormatRegistryService>(contextData, 'DataflowFormatRegistryService');
                const modelName = dataflowFormatRegistryService.getInlineSettingsModelName(format, this.source.sourceLinkedService);
                if (modelName && this.source.sourceLinkedService) {
                    const context = AdapterContext.defaultContextOf(contextData);
                    const adapter = context.dataflowDSLAdapterContext.adapterFor(Metamodel.modelByName(modelName));
                    this.source.datasetSettings = await adapter.toModel(
                        this.sourceObject.configs,
                        true,
                        this.source,
                        this._handledConfigs
                    );
                    this.source.isInlineDataset = true;
                }
                this.source.format = format;
            }
        }
        this.source.initialize(
            this.source.isInlineDataset &&
                this.source.datasetSettings &&
                this.source.datasetSettings instanceof CdmSettings
                ? DatasetFormatType.CDM
                : undefined
        );
        if (
            this.source.sourceDataset instanceof SapOdpDataset &&
            !this.sourceObject.configs.has('fixDecimalPrecision')
        ) {
            this.source.fixDecimalPrecision = false;
        }
        this.sourceObject.configs.forEach((expression: string, configName: string) => {
            if (this._handledConfigs.includes(configName)) {
                return;
            } else if (configName === 'limit') {
                this.source.rowsLimit = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.source,
                    IntegerDataFlowType.TYPE
                );
                this.source.sampling = true;
                this._handledConfigs.push(configName);
            } else if (configName === 'databaseType') {
                this._handledConfigs.push(configName);
                this.source.isWorkspaceDatabase = true;
                this.source.workspaceDatabaseType = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.source
                );
            } else if (configName === 'database') {
                this._handledConfigs.push(configName);
                this.source.workspaceDatabase = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.source
                );
            } else if (configName === 'tableName') {
                this._handledConfigs.push(configName);
                this.source.workspaceTable = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.source
                );
            } else if (configName === 'rowUrlColumn') {
                this.source.rowUrl = DataFlowExpressionUtils.unwrapExpressionConfigValue(expression, this.source);
                this._handledConfigs.push(configName);
            } else if (configName === 'modifiedAfter') {
                this.source.modifiedAfter = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.source,
                    TimeStampDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'modifiedBefore') {
                this.source.modifiedBefore = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.source,
                    TimeStampDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'partitionColumn') {
                // check is this a exprssion
                const unwrapedExpression = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.source,
                    StringDataFlowType.TYPE
                );
                if (stringIsNullOrEmpty(unwrapedExpression.templateExpression)) {
                    this.source.partitionColumn = new ColumnReference(
                        this.source.columns.find(
                            (col) =>
                                col.serializableColumnName ===
                                DataFlowExpressionUtils.extractStringConfigValue(expression)
                        )
                    );
                } else {
                    this.source.partitionColumn.dynamicPartitionColumn = unwrapedExpression;
                }
                this._handledConfigs.push(configName);
            } else if (configName === 'partitionPredicates') {
                if (
                    this.source.sourceDataset instanceof SapOdpDataset ||
                    this.source.datasetSettings instanceof SapOdpSettings
                ) {
                    const isExpression = expression.startsWith('(') && expression.endsWith(')');
                    if (isExpression) {
                        const unwrappedExpression: DataFlowExpressionArray<unknown> =
                            DataFlowExpressionUtils.unwrapExpressionArrayConfigValue(expression, this.source);
                        this.source.sapOdpPartitionPredicates = unwrappedExpression as DataFlowExpressionArray<
                            SapOdpPartitionPredicate[]
                        >;
                    } else {
                        this.source.sapOdpPartitionPredicates.expressions =
                            DataFlowExpressionUtils.parseScopeArrayInDsl(expression).map((predicate) =>
                                DataFlowExpressionUtils.parseScopeArrayInDsl(predicate).map((condition) => {
                                    const configs = DataFlowExpressionUtils.parseStringMapInDsl(condition);
                                    return SapOdpPartitionPredicate.toModel(this.source, configs);
                                })
                            );
                    }
                } else {
                    this.source.partitionPredicates = DataFlowExpressionUtils.parseExpressionArrayInDsl(
                        expression,
                        this.source
                    );
                }
                this._handledConfigs.push(configName);
                this.source.partitionOption = 'Partitioning';
                this.source.useQueryPartition = true;
            } else if (configName === 'numPartitions') {
                if (this.source.datasetSettings instanceof EventHubsSettings) {
                    this.source.partitionOption = 'Partitioning';
                    this.source.partitionMethod = 'external';
                    this.source.numberOfPartitions = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                        expression,
                        this.source,
                        IntegerDataFlowType.TYPE
                    );
                    this._handledConfigs.push(configName);
                }
            } else if (configName === 'maxRecordsPerPartition') {
                if (this.source.datasetSettings instanceof EventHubsSettings) {
                    // check if this is an expression
                    const unwrapedExpression = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                        expression,
                        this.source,
                        LongDataFlowType.TYPE
                    );
                    if (stringIsNullOrEmpty(unwrapedExpression.templateExpression)) {
                        const configValue = unwrapedExpression.expression;
                        const maxRecordsPerPartition = configValue.substring(0, configValue.length - 1);
                        this.source.maxRecordsPerPartition.expression = maxRecordsPerPartition;
                    } else {
                        this.source.maxRecordsPerPartition = unwrapedExpression;
                    }
                    this._handledConfigs.push(configName);
                }
            } else if (configName === 'partitionRootPath') {
                this.source.partitionRootPath = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.source
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'moveFiles') {
                this.source.completionAction = CompletionAction.MoveFile;
                this.source.moveFileList = DataFlowExpressionUtils.parseExpressionArrayInDsl(expression, this.source);
                this._handledConfigs.push(configName);
            } else if (configName === 'purgeFiles') {
                this.source.completionAction = CompletionAction.DeleteSource;
                this._handledConfigs.push(configName);
            } else if (configName === 'dateFormats') {
                this.source.dataTypeSettings.dateFormats = DataFlowExpressionUtils.parseStringArrayInDsl(expression);
                this._handledConfigs.push(configName);
            } else if (configName === 'timestampFormats') {
                this.source.dataTypeSettings.timestampFormats = DataFlowExpressionUtils.parseStringArrayInDsl(
                    expression
                ).map((value) => DataFlowExpressionUtils.unescapeSingleQuoteInDslString(value));
                this._handledConfigs.push(configName);
            } else if (configName === 'preferredIntegralType') {
                this.source.dataTypeSettings.preferredIntegralType =
                    DataFlowExpressionUtils.trimSpacesAndQuotesInDslString(expression);
                this._handledConfigs.push(configName);
            } else if (configName === 'preferredFractionalType') {
                this.source.dataTypeSettings.preferredFractionalType =
                    DataFlowExpressionUtils.trimSpacesAndQuotesInDslString(expression);
                this._handledConfigs.push(configName);
            } else if (configName === 'booleanFormat') {
                const booleanFormat = DataFlowExpressionUtils.parseStringArrayInDsl(expression);
                this.source.dataTypeSettings.booleanTrueFormat = booleanFormat[0];
                this.source.dataTypeSettings.booleanFalseFormat = booleanFormat[1];
                this._handledConfigs.push(configName);
            } else if (configName === 'wildcardPaths') {
                this.source.wildcardPaths = DataFlowExpressionUtils.parseExpressionArrayInDsl(expression, this.source);
                this._handledConfigs.push(configName);
            } else if (configName === 'staged') {
                this.source.staged = expression === 'true';
                this._handledConfigs.push(configName);
            } else if (configName === 'batchSize') {
                this.source.batchSize = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.source,
                    IntegerDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'consistencyLevel') {
                this.source.consistencyLevel = DataFlowExpressionUtils.unescapeSingleQuoteInDslString(
                    DataFlowExpressionUtils.trimSpacesAndQuotesInDslString(expression)
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'isolationLevel') {
                this.source.isolationLevel = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.source
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'systemColumns') {
                this.source.systemColumns = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.source,
                    BooleanDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'ignoreNoFilesFound') {
                this.source.ignoreNoFilesFound = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.source,
                    BooleanDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'throughput') {
                this.source.throughput = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.source,
                    IntegerDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'preferredRegions') {
                this.source.preferredRegions = DataFlowExpressionUtils.parseStringArrayInDsl(expression);
                this._handledConfigs.push(configName);
            } else if (configName === 'enableCdc') {
                this.source.enableCdc = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.source,
                    BooleanDataFlowType.TYPE
                );
                if (this.source.sourceDataset instanceof SapOdpDataset) {
                    this.source.sapOdpLegacyMode = true;
                }
                this._handledConfigs.push(configName);
            } else if (configName === 'enableNativeCdc') {
                this.source.enableNativeCdc = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.source,
                    BooleanDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'netChanges') {
                this.source.netChanges = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.source,
                    BooleanDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'skipInitialLoad') {
                this.source.enableSkipInitialLoad = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.source,
                    BooleanDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'waterMarkColumn') {
                this.source.waterMarkColumn = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.source,
                    StringDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'enableChangeFeed') {
                this.source.enableChangeFeed = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.source,
                    BooleanDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'changeFeedStartFromTheBeginning') {
                this.source.changeFeedStartFromBeginning = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.source,
                    BooleanDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'query') {
                this.source.useQuery = true;
                this.source.query = DataFlowExpressionUtils.unwrapExpressionConfigValue(expression, this.source);
                this._handledConfigs.push(configName);
                if (!this.source.query.templateExpression) {
                    DataFlowExpressionUtils.unescapeConfigValue(this.source.query);
                }
            } else if (configName === 'httpMethod') {
                this.source.requestMethod = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.source
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'body') {
                this.source.requestBody = DataFlowExpressionUtils.unwrapExpressionConfigValue(expression, this.source);
                this._handledConfigs.push(configName);
            } else if (configName === 'timeout') {
                const timeout = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.source,
                    IntegerDataFlowType.TYPE
                );
                if (
                    this.source.sourceDataset instanceof AzureDataExplorerDataset ||
                    this.source.sourceDataset instanceof DynamicsEntityDataset ||
                    this.source.sourceDataset instanceof DynamicsCrmEntityDataset ||
                    this.source.sourceDataset instanceof CommonDataServiceEntityDataset
                ) {
                    this.source.timeout = timeout;
                } else {
                    this.source.requestTimeout = timeout;
                }
                this._handledConfigs.push(configName);
            } else if (configName === 'httpRequestTimeout') {
                this.source.timeout = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.source,
                    IntegerDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'requestInterval') {
                this.source.requestInterval = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.source,
                    IntegerDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'headers') {
                this.source.additionalHeaders = expression
                    .slice(1, expression.length - 1)
                    .split(DataFlowAdapterUtils.MAP_ARRAY_SPLITTER)
                    .map((header) => {
                        const splits = header.split(DataFlowAdapterUtils.MAP_KEY_VALUE_SPLITTER);
                        const dataFlowParameterConfig = new DataFlowParameterConfig();
                        dataFlowParameterConfig.paramName = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                            splits[0].trim(),
                            this.source
                        );
                        dataFlowParameterConfig.paramValue = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                            splits[1].trim(),
                            this.source
                        );
                        return dataFlowParameterConfig;
                    });
                this._handledConfigs.push(configName);
            } else if (configName === 'queryParameters') {
                this.source.queryParameters = expression
                    .slice(1, expression.length - 1)
                    .split(DataFlowAdapterUtils.MAP_ARRAY_SPLITTER)
                    .map((para) => {
                        const splits = para.split(DataFlowAdapterUtils.MAP_KEY_VALUE_SPLITTER);
                        const dataFlowParameterConfig = new DataFlowParameterConfig();
                        dataFlowParameterConfig.paramName = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                            splits[0].trim(),
                            this.source
                        );
                        dataFlowParameterConfig.paramValue = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                            splits[1].trim(),
                            this.source
                        );
                        return dataFlowParameterConfig;
                    });
                this._handledConfigs.push(configName);
            } else if (configName === 'paginationRules') {
                this.source.paginationRules = expression
                    .slice(1, expression.length - 1)
                    .split(DataFlowAdapterUtils.MAP_ARRAY_SPLITTER)
                    .map((rule) => {
                        const splits = rule.split(DataFlowAdapterUtils.MAP_KEY_VALUE_SPLITTER);
                        const dataFlowParameterConfig = new DataFlowParameterConfig();
                        dataFlowParameterConfig.paramName = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                            splits[0].trim(),
                            this.source
                        );
                        dataFlowParameterConfig.paramValue = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                            splits[1].trim(),
                            this.source
                        );
                        return dataFlowParameterConfig;
                    });
                this._handledConfigs.push(configName);
            } else if (configName === 'responseFormat') {
                const formatSettings = expression
                    .slice(1, expression.length - 1)
                    .split(DataFlowAdapterUtils.MAP_ARRAY_SPLITTER);
                formatSettings.forEach((setting) => {
                    const [settingName, settingValue] = setting
                        .split(DataFlowAdapterUtils.MAP_KEY_VALUE_SPLITTER)
                        .map((v) => v.trim());
                    switch (settingName) {
                        case `'type'`:
                            this.source.restDataFormat = DataFlowExpressionUtils.extractStringConfigValue(
                                settingValue
                            ) as 'xml' | 'json';
                            break;
                        case `'documentForm'`:
                            this.source.documentForm = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                                settingValue,
                                this.source
                            );
                            break;
                        case `'nullValue'`:
                            this.source.nullValue = DataFlowExpressionUtils.extractStringConfigValue(settingValue);
                            break;
                        case `'namespaces'`:
                            this.source.namespaces = settingValue === 'true';
                            break;
                        case `'namespacePrefixes'`:
                            this.source.namespacePrefixes = settingValue
                                .slice(1, settingValue.length - 1)
                                .split(DataFlowAdapterUtils.MAP_ARRAY_SPLITTER)
                                .map((element) => {
                                    const splits = element.split(DataFlowAdapterUtils.MAP_KEY_VALUE_SPLITTER);
                                    return {
                                        key: DataFlowExpressionUtils.unwrapExpressionConfigValue(
                                            splits[0].trim(),
                                            this.source
                                        ).expression,
                                        val: DataFlowExpressionUtils.unwrapExpressionConfigValue(
                                            splits[1].trim(),
                                            this.source
                                        ).expression
                                    };
                                });
                            break;
                        default:
                    }
                });
                this._handledConfigs.push(configName);
            } else if (
                configName === 'readMode' &&
                (this.source.sourceDataset instanceof SapOdpDataset ||
                    this.source.sourceDataset instanceof SapODataDataset)
            ) {
                this.source.sapReadMode = DataFlowExpressionUtils.unwrapExpressionConfigValue(expression, this.source);
                this._handledConfigs.push(configName);
            } else if (
                configName === 'keys' &&
                (this.source.sourceDataset instanceof SapOdpDataset ||
                    this.source.sourceDataset instanceof SapODataDataset)
            ) {
                this.source.sapOdpKeyColumns = DataFlowExpressionUtils.unwrapExpressionArrayConfigValue(
                    expression,
                    this.source
                );
            } else if (configName === 'skipLines') {
                this.source.skipLineCount = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.source,
                    IntegerDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'validationMode') {
                this.source.validationMode = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.source
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'namespaces') {
                this.source.namespaces = expression === 'true';
                this._handledConfigs.push(configName);
            } else if (configName === 'namespacePrefixes') {
                this.source.namespacePrefixes = expression
                    .slice(1, expression.length - 1)
                    .split(DataFlowAdapterUtils.MAP_ARRAY_SPLITTER)
                    .map((element) => {
                        const splits = element.split(DataFlowAdapterUtils.MAP_KEY_VALUE_SPLITTER);
                        return {
                            key: DataFlowExpressionUtils.unwrapExpressionConfigValue(splits[0].trim(), this.source)
                                .expression,
                            val: DataFlowExpressionUtils.unwrapExpressionConfigValue(splits[1].trim(), this.source)
                                .expression
                        };
                    });
                this._handledConfigs.push(configName);
            } else if (configName === 'documentForm') {
                this.source.documentForm = DataFlowExpressionUtils.unwrapExpressionConfigValue(expression, this.source);
                this._handledConfigs.push(configName);
            } else if (configName === 'singleDocument') {
                this.source.documentForm.expression =
                    expression === 'true' ? DocumentForm.singleDocument : DocumentForm.documentPerLine;
                this._handledConfigs.push(configName);
            } else if (configName === 'unquotedColumnNames') {
                this.source.unquotedColumnNames = expression === 'true';
                this._handledConfigs.push(configName);
            } else if (configName === 'hasComments') {
                this.source.hasComments = expression === 'true';
                this._handledConfigs.push(configName);
            } else if (configName === 'singleQuoted') {
                this.source.singleQuoted = expression === 'true';
                this._handledConfigs.push(configName);
            } else if (configName === 'backslashEscape') {
                this.source.backslashEscape = expression === 'true';
                this._handledConfigs.push(configName);
            } else if (configName === 'procedureName') {
                this.source.storedProcName = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.source,
                    StringDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'schemaName') {
                this.source.schemaName = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.source,
                    StringDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'resultSet') {
                this.source.storedProcResultSet = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.source,
                    BooleanDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'inputs') {
                this.source.storedProcInputs = DataFlowExpressionUtils.parseExpressionMapInDsl(
                    expression,
                    this.source,
                    StringDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'maxColumns') {
                this.source.maxColumns = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.source,
                    NumberDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else if (
                [
                    'validateSchema',
                    'allowSchemaDrift',
                    'fileList',
                    'inferDriftedColumnTypes',
                    'multiLineRow',
                    'useSchema'
                ].includes(configName)
            ) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (this.source as any)[configName] = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.source,
                    BooleanDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (this.source as any)[configName] = expression === 'true';
            }
        });
        super.postConvert();
        return this.source;
    }

    public convertInputStream(): void {
        // do nothing
    }
}
